import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { Faq } from "@/components/Faq";
import { Button } from "@/components/ui/button";
import { breadcrumbSchema, faqSchema, jsonLd, absUrl } from "@/lib/seo";
import { GENERIC_BOKADIREKT } from "@/lib/booking";

const BOOK_URL = GENERIC_BOKADIREKT;

const values = [
  { name: "Herrklippning", price: "450 kr" },
  { name: "Taper Fade / Skin Fade", price: "450 kr" },
  { name: "Skäggtrimning", price: "380 kr" },
  { name: "Herrklippning + Skäggtrimning", price: "680 kr" },
  { name: "Valfritt belopp", price: "—" },
];

const faq = [
  { q: "Var kan jag köpa ett presentkort till Tailor's Barber?", a: "I valfri salong – Täby Centrum, Åhléns City, Åkersberga eller Arninge." },
  { q: "På vilka salonger kan presentkortet användas?", a: "På alla fyra salonger i Stockholm." },
  { q: "Hur länge är presentkortet giltigt?", a: "12 månader från köpdatum." },
  { q: "Kan jag köpa ett presentkort online?", a: "Kontakta oss via BokaDirekt för digitalt presentkort." },
  { q: "Kan mottagaren använda presentkortet på drop-in?", a: "Ja, presentkortet fungerar både vid onlinebokning och drop-in." },
];

export const Route = createFileRoute("/presentkort")({
  head: () => ({
    meta: [
      { title: "Presentkort barber Stockholm | Tailor's Barber" },
      { name: "description", content: "Ge bort ett presentkort hos Tailor's Barber i Stockholm – herrklippning, fade eller skäggtrimning. Giltigt på alla fyra salonger i 12 månader." },
      { property: "og:title", content: "Presentkort barber Stockholm | Tailor's Barber" },
      { property: "og:description", content: "Den perfekta manspresenten. Giltigt på alla fyra salonger." },
      { property: "og:url", content: absUrl("/presentkort") },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: absUrl("/presentkort") }],
    scripts: [
      jsonLd(faqSchema(faq)),
      jsonLd(breadcrumbSchema([{ name: "Hem", url: "/" }, { name: "Presentkort", url: "/presentkort" }])),
    ],
  }),
  component: PresentkortPage,
});

function PresentkortPage() {
  return (
    <SiteShell>
      <section className="container-page pt-10 md:pt-16 pb-4">
        <nav className="text-xs text-muted-foreground mb-6">
          <Link to="/" className="hover:underline">Hem</Link>
          <span className="mx-1.5">/</span>
          <span className="text-foreground">Presentkort</span>
        </nav>
        <div className="eyebrow">Presentkort Stockholm</div>
        <h1 className="mt-3 text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
          Presentkort hos Tailor&apos;s Barber – Den Perfekta Manspresenten
        </h1>
        <p className="mt-5 text-lg text-foreground/80 max-w-prose leading-relaxed">
          Vet du inte vad du ska ge? Ge bort upplevelsen av en professionell klippning eller
          skäggtrimning hos Tailor&apos;s Barber. Våra presentkort är giltiga på alla fyra salonger i
          Stockholm och kan användas för valfri behandling.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Varför ett presentkort hos Tailor&apos;s Barber?</h2>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Det är en present som alltid passar. Oavsett stil, ålder eller hårtyp – alla män uppskattar
          en välgjord klippning. Presentkortet ger mottagaren frihet att boka den behandling och den
          salong som passar dem bäst, när det passar dem.
        </p>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          <strong>Passar till:</strong> Födelsedag, jul, midsommar, fars dag, studenten, alla hjärtans
          dag, eller bara för att.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Hur köper jag ett presentkort?</h2>
        <div className="mt-6 grid gap-5 md:grid-cols-2 max-w-4xl">
          <div className="card-elev p-6">
            <h3 className="text-lg font-medium">I salong</h3>
            <p className="mt-2 text-foreground/80 leading-relaxed">
              Köp presentkort direkt i kassan på valfri salong – Täby Centrum, Åhléns City, Åkersberga
              eller Arninge.
            </p>
          </div>
          <div className="card-elev p-6">
            <h3 className="text-lg font-medium">Online</h3>
            <p className="mt-2 text-foreground/80 leading-relaxed">
              Kontakta oss via{" "}
              <a href={BOOK_URL} target="_blank" rel="noopener noreferrer" className="underline">
                BokaDirekt
              </a>{" "}
              för digitalt presentkort.
            </p>
          </div>
        </div>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Presentkortets värde</h2>
        <p className="mt-4 text-foreground/80 max-w-3xl">
          Välj ett valfritt belopp eller välj en specifik behandling:
        </p>
        <ul className="mt-6 divide-y divide-border/60 max-w-2xl">
          {values.map((v) => (
            <li key={v.name} className="flex items-center justify-between py-3">
              <span className="text-base md:text-lg text-foreground/85">{v.name}</span>
              <span className="text-base md:text-lg font-medium">{v.price}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Giltighet och villkor</h2>
        <ul className="mt-4 space-y-2 list-disc list-inside text-base md:text-lg text-foreground/85 max-w-3xl">
          <li>Presentkortet är giltigt i 12 månader från köpdatum.</li>
          <li>Kan användas på alla fyra salonger.</li>
          <li>Kan inte bytas mot kontanter.</li>
          <li>Kan kombineras med drop-in eller onlinebokning.</li>
        </ul>
      </section>

      <Faq items={faq} heading="Vanliga frågor om presentkort" />

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Relaterade sidor</h2>
        <ul className="mt-4 flex flex-wrap gap-3">
          <li><Link to="/priser/" className="btn-outline">Prislista</Link></li>
          <li><Link to="/herrklippning/" className="btn-outline">Herrklippning</Link></li>
          <li><Link to="/" className="btn-outline">Barberare Stockholm</Link></li>
        </ul>
      </section>

      <section className="container-page pb-16">
        <div className="card-elev p-8 md:p-10 text-center">
          <h2 className="text-2xl md:text-3xl">Hitta närmaste salong</h2>
          <p className="mt-3 text-foreground/80">Fyra salonger i Stockholm – köp ditt presentkort i kassan.</p>
          <div className="mt-6">
            <Button asChild size="lg">
              <Link to="/">Se alla salonger</Link>
            </Button>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
